import { useCallback, useEffect, useRef, useState } from 'react';

const API = process.env.REACT_APP_BACKEND_URL;
export const fileUrl = (path) => (path ? `${API}/api/files/${path}` : '');

const parseError = async (res, fallback) => {
  try { const j = await res.json(); return j.detail || fallback; } catch { return fallback; }
};

export const useVisualizer = () => {
  const [options, setOptions] = useState(null);
  const [session, setSession] = useState(null);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const timer = useRef(null);

  useEffect(() => {
    fetch(`${API}/api/visualizer/options`).then((r) => r.json()).then(setOptions).catch(() => {});
    return () => clearTimeout(timer.current);
  }, []);

  const pending = !!session?.generations?.some((g) => g.status === 'pending');

  useEffect(() => {
    if (!session || !pending) return;
    timer.current = setTimeout(async () => {
      try {
        const r = await fetch(`${API}/api/visualizer/sessions/${session.id}`);
        if (r.ok) setSession(await r.json());
      } catch { /* retry on next tick */ }
    }, 3000);
    return () => clearTimeout(timer.current);
  }, [session, pending]);

  const upload = useCallback(async (file) => {
    setBusy(true); setError('');
    try {
      const fd = new FormData(); fd.append('photo', file);
      const r = await fetch(`${API}/api/visualizer/sessions`, { method: 'POST', body: fd });
      if (!r.ok) throw new Error(await parseError(r, 'Upload mislukt'));
      setSession(await r.json());
    } catch (e) { setError(e.message); } finally { setBusy(false); }
  }, []);

  const addReference = useCallback(async (file) => {
    if (!session) return;
    setBusy(true); setError('');
    try {
      const fd = new FormData(); fd.append('photo', file);
      const r = await fetch(`${API}/api/visualizer/sessions/${session.id}/references`, { method: 'POST', body: fd });
      if (!r.ok) throw new Error(await parseError(r, 'Upload mislukt'));
      setSession(await r.json());
    } catch (e) { setError(e.message); } finally { setBusy(false); }
  }, [session]);

  const generate = useCallback(async (room, choices, extra, baseGenerationId) => {
    if (!session) return;
    setBusy(true); setError('');
    try {
      const fd = new FormData();
      fd.append('ruimte', room || 'andere');
      fd.append('choices', JSON.stringify(choices || {}));
      fd.append('extra', extra || '');
      if (baseGenerationId) fd.append('base_generation_id', baseGenerationId);
      const r = await fetch(`${API}/api/visualizer/sessions/${session.id}/generate`, { method: 'POST', body: fd });
      if (!r.ok) throw new Error(await parseError(r, 'Genereren mislukt'));
      setSession(await r.json());
    } catch (e) { setError(e.message); } finally { setBusy(false); }
  }, [session]);

  const submitLead = useCallback(async (form) => {
    const r = await fetch(`${API}/api/visualizer/sessions/${session.id}/lead`, {
      method: 'POST', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ ...form, sourceUrl: window.location.href }),
    });
    if (!r.ok) throw new Error(await parseError(r, 'Verzenden mislukt'));
    return r.json();
  }, [session]);

  const reset = () => { setSession(null); setError(''); };

  return { options, session, error, busy, pending, upload, addReference, generate, submitLead, reset, applySession: setSession };
};
